import type { Customer } from '@payaca/types/customerTypes';
import type { Invoice } from '@payaca/types/invoiceTypes';
import type {
  JobPayment,
  PaymentMethodType,
} from '@payaca/types/jobPaymentTypes';
import type { FetchedEntity } from '@payaca/types/storeTypes';
import type { Nullish } from '@payaca/utilities/types';
import type { DeclareAsyncAction } from '../types';

export interface State {
  isRecordingJobPayment: boolean;
  isJobPaymentRecordedSuccessfully?: boolean;
  isConfirmingJobPayment: boolean;
  isJobPaymentConfirmedSuccessfully?: boolean;

  jobPayments: { [key: number]: FetchedEntity<JobPayment> };

  isPersistingJobPayment: boolean;
}

export enum ActionType {
  REQUEST_RECORD_JOB_PAYMENT = 'jobPayments.requestRecordJobPayment',
  RECORD_JOB_PAYMENT_SUCCESS = 'jobPayments.recordJobPaymentSuccess',
  RECORD_JOB_PAYMENT_FAILURE = 'jobPayments.recordJobPaymentFailure',

  REQUEST_CONFIRM_JOB_PAYMENT = 'jobPayments.requestConfirmJobPayment',
  CONFIRM_JOB_PAYMENT_SUCCESS = 'jobPayments.confirmJobPaymentSuccess',
  CONFIRM_JOB_PAYMENT_FAILURE = 'jobPayments.confirmJobPaymentFailure',

  REQUEST_GET_JOB_PAYMENTS_FOR_DEAL = 'jobPayments.requestGetJobPaymentsForDeal',
  GET_JOB_PAYMENTS_FOR_DEAL_SUCCESS = 'jobPayments.getJobPaymentsForDealSuccess',

  CLEAR_JOB_PAYMENT = 'jobPayments.clearJobPayment',
  CLEAR_JOB_PAYMENTS = 'jobPayments.clearJobPayments',

  PERSIST_JOB_PAYMENT_REQUEST = 'jobPayments.persistJobPaymentRequest',
  PERSIST_JOB_PAYMENT_SUCCESS = 'jobPayments.persistJobPaymentSuccess',
  PERSIST_JOB_PAYMENT_FAILURE = 'jobPayments.persistJobPaymentFailure',

  PAY_INVOICES_REQUEST = 'jobPayments.payInvoices:request',
}

export type PayInvoicesRequestData = {
  customerId: Customer['id'];
  invoiceIds: Invoice['id'][];
  paymentMethodType: PaymentMethodType;
  paymentDate: Date;
  paymentNote?: Nullish<string>;
  sendReceipt?: boolean;
};

export interface SagaConfig {
  apiBaseurl: string;
  getAuthHeader: () => Promise<string>;
  isNativeApp: boolean;
}

export interface RecordJobPaymentRequestData {
  dealId: number;
  invoiceId?: number;
  sendReceipt: boolean;
  isDepositPayment: boolean;
  paymentMethod: PaymentMethodType;
  paymentValue: number;
  note?: string;
  paymentDate?: Date;
}

export interface ConfirmJobPaymentRequestData {
  jobPaymentId: number;
  isPaymentReceived: boolean;
  note?: string;
}

export interface PersistJobPaymentRequestData {
  dealId: number;
  paymentValue: number;
  isDepositPayment: boolean;
  invoiceId?: number;
  note?: string;
}

export type PayInvoices = DeclareAsyncAction<
  ActionType,
  {
    request: {
      type: ActionType.PAY_INVOICES_REQUEST;
      payload: {
        requestData: PayInvoicesRequestData;
        callback?: () => void;
        onErrorCallback?: () => void;
      };
    };
  }
>;

export type PersistJobPayment = DeclareAsyncAction<
  ActionType,
  {
    request: {
      type: ActionType.PERSIST_JOB_PAYMENT_REQUEST;
      payload: {
        dealId: number;
        isDepositPayment: boolean;
        paymentValue: number;
        paymentIntentId: string;
        invoiceId?: number;
        note?: string;
        callback?: () => void;
      };
    };
    success: {
      type: ActionType.PERSIST_JOB_PAYMENT_SUCCESS;
    };
    failure: {
      type: ActionType.PERSIST_JOB_PAYMENT_FAILURE;
    };
  }
>;
